import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { $t, updatePreset, updateSurfacePalette } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import Lara from '@primeng/themes/lara';
import Nora from '@primeng/themes/nora';
import { IThemeSettings } from '../../models/theme-preference';
import { SurfacePalette, THEME_PRIMARY_COLORS, THEME_SURFACES } from './theme.constants';

const presets = {
    Aura,
    Lara,
    Nora
} as const;

type PresetKey = keyof typeof presets;
type PaletteKey = keyof (typeof presets)[PresetKey]['primitive'];

@Injectable({
    providedIn: 'root'
})
export class ThemeApplyService {
    private readonly platformId = inject(PLATFORM_ID);

    applyFromConfig(config: IThemeSettings): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const preset = presets[config.preset as PresetKey] ?? presets.Aura;
        const surfacePalette = THEME_SURFACES.find((s) => s.name === config.surface)?.palette;
        $t().preset(preset).preset(this.getPresetExt(config)).surfacePalette(surfacePalette).use({ useDefaultOptions: true });
    }

    applyPrimaryChange(config: IThemeSettings): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        updatePreset(this.getPresetExt(config));
    }

    applySurfaceChange(surfaceName: string): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const surface = THEME_SURFACES.find((s) => s.name === surfaceName);
        if (surface?.palette) {
            updateSurfacePalette(surface.palette);
        }
    }

    getPrimaryColors(presetName: string): SurfacePalette[] {
        const preset = presets[presetName as PresetKey] ?? presets.Aura;
        const presetPalette = preset.primitive;
        const palettes: SurfacePalette[] = [{ name: 'noir', palette: {} }];

        THEME_PRIMARY_COLORS.filter((color) => color !== 'noir').forEach((color) => {
            palettes.push({
                name: color,
                palette: (presetPalette?.[color as PaletteKey] ?? {}) as Record<number, string>
            });
        });

        return palettes;
    }

    getPresetExt(config: IThemeSettings): object {
        const primaryColors = this.getPrimaryColors(config.preset);
        const color = primaryColors.find((c) => c.name === config.primary) ?? primaryColors[0];

        if (color.name === 'noir') {
            return {
                semantic: {
                    primary: {
                        50: '{surface.50}',
                        100: '{surface.100}',
                        200: '{surface.200}',
                        300: '{surface.300}',
                        400: '{surface.400}',
                        500: '{surface.500}',
                        600: '{surface.600}',
                        700: '{surface.700}',
                        800: '{surface.800}',
                        900: '{surface.900}',
                        950: '{surface.950}'
                    },
                    colorScheme: {
                        light: {
                            primary: {
                                color: '{primary.950}',
                                contrastColor: '#ffffff',
                                hoverColor: '{primary.800}',
                                activeColor: '{primary.700}'
                            },
                            highlight: {
                                background: '{primary.950}',
                                focusBackground: '{primary.700}',
                                color: '#ffffff',
                                focusColor: '#ffffff'
                            }
                        },
                        dark: {
                            primary: {
                                color: '{primary.50}',
                                contrastColor: '{primary.950}',
                                hoverColor: '{primary.200}',
                                activeColor: '{primary.300}'
                            },
                            highlight: {
                                background: '{primary.50}',
                                focusBackground: '{primary.300}',
                                color: '{primary.950}',
                                focusColor: '{primary.950}'
                            }
                        }
                    }
                }
            };
        }

        if (config.preset === 'Nora') {
            return {
                semantic: {
                    primary: color.palette,
                    colorScheme: {
                        light: {
                            primary: {
                                color: '{primary.600}',
                                contrastColor: '#ffffff',
                                hoverColor: '{primary.700}',
                                activeColor: '{primary.800}'
                            },
                            highlight: {
                                background: '{primary.600}',
                                focusBackground: '{primary.700}',
                                color: '#ffffff',
                                focusColor: '#ffffff'
                            }
                        },
                        dark: {
                            primary: {
                                color: '{primary.500}',
                                contrastColor: '{surface.900}',
                                hoverColor: '{primary.400}',
                                activeColor: '{primary.300}'
                            },
                            highlight: {
                                background: '{primary.500}',
                                focusBackground: '{primary.400}',
                                color: '{surface.900}',
                                focusColor: '{surface.900}'
                            }
                        }
                    }
                }
            };
        }

        return {
            semantic: {
                primary: color.palette,
                colorScheme: {
                    light: {
                        primary: {
                            color: '{primary.500}',
                            contrastColor: '#ffffff',
                            hoverColor: '{primary.600}',
                            activeColor: '{primary.700}'
                        },
                        highlight: {
                            background: '{primary.50}',
                            focusBackground: '{primary.100}',
                            color: '{primary.700}',
                            focusColor: '{primary.800}'
                        }
                    },
                    dark: {
                        primary: {
                            color: '{primary.400}',
                            contrastColor: '{surface.900}',
                            hoverColor: '{primary.300}',
                            activeColor: '{primary.200}'
                        },
                        highlight: {
                            background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                            focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                            color: 'rgba(255,255,255,.87)',
                            focusColor: 'rgba(255,255,255,.87)'
                        }
                    }
                }
            }
        };
    }
}
